/**
 * "Cookie settings" links the site already has, opening the preference centre.
 *
 * A site switching from another CMP keeps the footer link it wired for that
 * CMP, and the link then does nothing: on 7 Oct 2026 the Jeppesen test site's
 * "Cookie Settings" links carried OneTrust's ot-sdk-show-settings class and
 * href="#cookie-settings", and visitors could not reach their choices again
 * once the banner closed. Recognising those links here fixes every such site
 * from the core, with no snippet or site change.
 *
 * data-pb-preferences is the documented hook for any other element.
 */

/** Hooks of another CMP. Taken over only while that CMP is not running. */
const FOREIGN_CMP_SELECTOR = [
  ".ot-sdk-show-settings", // OneTrust
  "#ot-sdk-btn", // OneTrust
  ".optanon-show-settings", // OneTrust, legacy Optanon
].join(",");

/** In-page anchors sites conventionally use for a cookie settings link. */
const SETTINGS_HASHES = ["#cookie-settings", "#cookie-preferences", "#privacy-settings"];

const OWN_SELECTOR = "[data-pb-preferences]";

function foreignCmpRunning(win: Window): boolean {
  return typeof (win as unknown as { OneTrust?: unknown }).OneTrust !== "undefined";
}

/**
 * The element on the click's path that should open the preference centre, if
 * any. Walks composedPath() so a link inside a shadow root (Salesforce LWR,
 * Lit and other web-component footers) is still found from the document.
 */
export function findPreferenceTrigger(event: Event, doc: Document = document): Element | null {
  const path = typeof event.composedPath === "function" ? event.composedPath() : [event.target];
  const win = doc.defaultView ?? window;
  for (const node of path) {
    if (!(node instanceof Element)) continue;
    if (node.matches(OWN_SELECTOR)) return node;
    if (node.matches(FOREIGN_CMP_SELECTOR) && !foreignCmpRunning(win)) return node;
    if (node.tagName === "A") {
      const href = node.getAttribute("href");
      // A real section with that id is an in-page anchor, not a settings link.
      if (href && SETTINGS_HASHES.includes(href) && !doc.getElementById(href.slice(1))) {
        return node;
      }
    }
  }
  return null;
}

/**
 * Listens on the document in the capture phase, so the site's own handlers
 * (a router turning the click into a navigation, an empty OneTrust hook)
 * cannot swallow the click first. Returns a function that removes the listener.
 */
export function installPreferenceLinks(open: () => void, doc: Document = document): () => void {
  const onClick = (event: Event) => {
    if (!findPreferenceTrigger(event, doc)) return;
    event.preventDefault();
    event.stopPropagation();
    open();
  };
  doc.addEventListener("click", onClick, true);
  return () => doc.removeEventListener("click", onClick, true);
}
